import { captureError } from 'infra/monitoring/monitoring.utils';

// Vite's preload helper waits for load or error on every CSS dep before it runs the
// dynamic import, and a dep that emits neither leaves the route pending forever.
//
// This is a backstop, not a loading budget. The cause we actually hit was the Cookiebot
// listener swallow described in index.html, and the bypass there fixes it at the source, so
// the spinner a user sees is now the real download rather than this deadline running out.
//
// It stays generous because it cannot tell "stalled" from "slow", and firing early is paid by
// the users on the worst connections. The timeline route alone awaits about 69 KB of gzipped
// CSS, which is roughly 3.3 s on an idle slow-3G link and considerably more once it contends
// with the app's own chunks, so rural 3G can legitimately sit here for many seconds. Tripping
// early would emit a LazyRouteImportError that masks a genuine recurrence, and would let the
// route paint before its stylesheets arrive. If the stall ever comes back, that monitoring
// event tells us within hours; we do not need a short deadline to find out.
const DEFAULT_STALL_TIMEOUT_IN_MILLISECONDS = 15000;

const STALLED_IMPORT_MESSAGE = 'lazy route import stalled';

const importWithStallTimeout = <T>(importModule: () => Promise<T>, timeoutInMilliseconds: number) => {
  let stallTimer: ReturnType<typeof setTimeout>;

  const rejectWhenStalled = new Promise<never>((_, reject) => {
    stallTimer = setTimeout(() => reject(new Error(STALLED_IMPORT_MESSAGE)), timeoutInMilliseconds);
  });

  const runImport = Promise.resolve().then(importModule);

  return Promise.race([runImport, rejectWhenStalled]).finally(() => clearTimeout(stallTimer));
};

export const lazyWithStalledPreloadRetry = <T>(
  importModule: () => Promise<T>,
  timeoutInMilliseconds = DEFAULT_STALL_TIMEOUT_IN_MILLISECONDS
) => {
  return () =>
    importWithStallTimeout(importModule, timeoutInMilliseconds).catch((firstAttemptError: unknown) => {
      captureError(firstAttemptError, { errorType: 'LazyRouteImportError', domain: 'Architecture' });

      // Vite's preload helper marks every dep as seen on the first attempt, so the retry skips
      // the CSS wait and only waits on the network. It runs without a deadline on purpose: a
      // second one would turn a merely slow chunk into a rejection, and React.lazy would then
      // throw the navigation into the app-level crash screen instead of keeping the spinner up.
      return importModule();
    });
};
